import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';
import { Plus, Minus, X, CheckCircle2, Edit2, Trash2, Eye, FileText, Shield, ShieldCheck, ShieldAlert, ShieldQuestion, Mail } from 'lucide-react';
import api from '../../../api.js';
import { insuranceModuleAPI } from '../../../api/insurance.js';
import VehicleInsuranceForm from '../forms/VehicleInsuranceForm.jsx';
import ContractDetailModal from '../modals/ContractDetailModal.jsx';
import VehicleAddTool from '../modals/VehicleAddTool.jsx';
import VehicleRemoveTool from '../modals/VehicleRemoveTool.jsx';
import InsuranceVerificationModal from '../modals/InsuranceVerificationModal.jsx';

function VehicleInsuranceTab({ onMessage }) {
  const { t } = useTranslation();
  // Devise de l'organisation de la ligne - un seul outil monetaire.
  const { money } = useOrgCurrency();
  const [vehicleInsurance, setVehicleInsurance] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [coverageModels, setCoverageModels] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('all'); // 'all' or org_id
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showAddTool, setShowAddTool] = useState(false);
  const [showRemoveTool, setShowRemoveTool] = useState(false);
  const [selectedContractForTool, setSelectedContractForTool] = useState(null);
  const [verificationStatuses, setVerificationStatuses] = useState({});
  const [verificationSummary, setVerificationSummary] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verifyingVi, setVerifyingVi] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [viRes, vRes, cRes, orgRes, cmRes] = await Promise.all([
        api.vehicleInsurance.getAll(),
        api.vehicle.getAll(),
        api.insurance_contract.getAll(),
        api.organization.getAll(),
        api.coverageModel.getAll().catch(() => ({ ok: false })),
      ]);

      if (viRes.ok) setVehicleInsurance(viRes.data);
      if (vRes.ok) setVehicles(vRes.data);
      if (cRes.ok) setContracts(cRes.data);
      if (orgRes.ok) setOrganizations(orgRes.data);
      if (cmRes.ok) setCoverageModels(cmRes.data || []);

      // Load verification statuses (all — not just unverified)
      try {
        const [summaryRes, allStatusesRes] = await Promise.all([
          insuranceModuleAPI.getVerificationSummary(),
          insuranceModuleAPI.getAllVerificationStatuses(),
        ]);
        if (summaryRes.ok) setVerificationSummary(summaryRes.data);
        if (allStatusesRes.ok && allStatusesRes.data) {
          setVerificationStatuses(allStatusesRes.data);
        }
      } catch (verErr) {
        console.warn('Could not load verification statuses:', verErr.message);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      onMessage?.(t('insurance.vehicleTab.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter vehicle insurance by organization, excluding orphaned and removed/terminated records
  const vehicleIds = new Set(vehicles.map(v => v.id));
  const filteredVehicleInsurance = (
    selectedOrg === 'all'
      ? vehicleInsurance
      : vehicleInsurance.filter(vi => vi.org_id === selectedOrg)
  ).filter(vi => vehicleIds.has(vi.vehicle_id) && !vi.removed_at);

  const handleCreate = () => {
    setEditingItem(null);
    setShowForm(true);
  };

  const handleEdit = item => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleDelete = async id => {
    if (!window.confirm(t('insurance.vehicleTab.confirmRemove'))) return;

    try {
      const res = await insuranceModuleAPI.removeVehicle(id, {});
      if (res.ok) {
        onMessage?.(t('insurance.vehicleTab.removed'), 'success');
        loadData();
      } else {
        onMessage?.(t('insurance.vehicleTab.removeFailed'), 'error');
      }
    } catch {
      onMessage?.(t('insurance.vehicleTab.removeFailed'), 'error');
    }
  };

  const handleSave = async formData => {
    let res;
    if (editingItem) {
      res = await api.vehicleInsurance.update(editingItem.id, formData);
    } else {
      // Use module API for new assignments (triggers dual-contract guard + verification)
      res = await insuranceModuleAPI.assignVehicle({
        vehicle_id: formData.vehicle_id,
        contract_id: formData.contract_id,
        premium_amount: formData.premium_amount || null,
        deductible_amount: formData.deductible_amount || null,
        assigned_at: formData.assigned_at || new Date().toISOString().split('T')[0],
        is_primary: formData.is_primary ?? true,
      });
    }

    if (res.ok) {
      onMessage?.(editingItem ? t('insurance.vehicleTab.updated') : t('insurance.vehicleTab.created'), 'success');
      setShowForm(false);
      loadData();
    } else {
      onMessage?.(t('insurance.vehicleTab.saveFailed'), 'error');
    }
  };

  const getVehicleInfo = vehicleId => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? `${vehicle.license_plate} - ${vehicle.make} ${vehicle.model}` : t('common.states.notAvailable');
  };

  const getContractInfo = contractId => {
    const contract = contracts.find(c => c.id === contractId);
    return contract ? contract.policy_number : t('common.states.notAvailable');
  };

  const getContract = contractId => contracts.find(c => c.id === contractId);

  const handleViewContract = contractId => {
    const contract = getContract(contractId);
    if (contract) {
      setSelectedContract(contract);
    }
  };

  const handleBulkAdd = contract => {
    setSelectedContractForTool(contract);
    setShowAddTool(true);
  };

  const handleBulkRemove = contract => {
    setSelectedContractForTool(contract);
    setShowRemoveTool(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">{t('common.states.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">{t('insurance.vehicleTab.title')}</h2>
        <div className="flex items-center gap-2">
          {/* Bulk Operations by Contract */}
          {contracts.length > 0 && (
            <div className="flex items-center gap-2 mr-2 border-r border-gray-300 pr-4">
              <select
                onChange={e => {
                  const contract = contracts.find(c => c.id === parseInt(e.target.value));
                  if (contract) setSelectedContractForTool(contract);
                }}
                value={selectedContractForTool?.id || ''}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[200px]"
              >
                <option value="">{t('insurance.vehicleTab.selectContract')}</option>
                {contracts.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.policy_number} - {c.insurer_name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => selectedContractForTool && handleBulkAdd(selectedContractForTool)}
                disabled={!selectedContractForTool}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium ${
                  selectedContractForTool
                    ? 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                title={t('insurance.vehicleTab.bulkAddTitle')}
              >
                <Plus className="w-4 h-4" />
                {t('common.actions.add')}
              </button>
              <button
                onClick={() => selectedContractForTool && handleBulkRemove(selectedContractForTool)}
                disabled={!selectedContractForTool}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium ${
                  selectedContractForTool
                    ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                title={t('insurance.vehicleTab.bulkRemoveTitle')}
              >
                <Minus className="w-4 h-4" />
                {t('insurance.vehicleTab.remove')}
              </button>
            </div>
          )}
          <button
            onClick={handleCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('insurance.vehicleTab.singleAssignment')}
          </button>
        </div>
      </div>

      {/* Verification Summary Bar */}
      {verificationSummary && (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">{t('insurance.vehicleTab.verificationTitle')}</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-green-700">
              <ShieldCheck className="w-4 h-4 inline mr-1" />{t('insurance.vehicleTab.verifiedCount', { count: verificationSummary.verified_ok })}
            </span>
            {verificationSummary.verified_discrepancy > 0 && (
              <span className="text-red-700">
                <ShieldAlert className="w-4 h-4 inline mr-1" />{t('insurance.vehicleTab.discrepancyCount', { count: verificationSummary.verified_discrepancy })}
              </span>
            )}
            <span className="text-gray-500">
              <ShieldQuestion className="w-4 h-4 inline mr-1" />{t('insurance.vehicleTab.notVerifiedCount', { count: verificationSummary.not_verified + (verificationSummary.expired || 0) })}
            </span>
          </div>
          {(verificationSummary.not_verified + (verificationSummary.expired || 0)) > 0 && (
            <div className="ml-auto text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-md flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" />
              {t('insurance.vehicleTab.askBrokerHint')}
            </div>
          )}
        </div>
      )}

      {/* Organization Filter */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">{t('insurance.vehicleTab.filterByOrg')}</label>
          <select
            value={selectedOrg}
            onChange={e => setSelectedOrg(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">{t('insurance.vehicleTab.wholeFleet')}</option>
            {organizations.map(org => (
              <option key={org.code} value={org.code}>
                {org.name}
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-500">
            {t('insurance.vehicleTab.vehicleCount', { count: filteredVehicleInsurance.length })}
          </span>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-xs">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-3 text-left font-semibold text-gray-700 whitespace-nowrap sticky left-0 bg-gray-50 z-10">
                {t('insurance.vehicleTab.col.vehicle')}
              </th>
              <th className="px-2 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">
                {t('insurance.vehicleTab.col.contract')}
              </th>
              <th className="px-2 py-3 text-center font-semibold text-gray-700 whitespace-nowrap">
                {t('insurance.vehicleTab.col.coverageModel')}
              </th>
              <th className="px-2 py-3 text-center font-semibold text-gray-700 whitespace-nowrap">
                {t('insurance.vehicleTab.col.premium')}
              </th>
              <th className="px-2 py-3 text-center font-semibold text-gray-700 whitespace-nowrap">
                {t('insurance.vehicleTab.col.deductible')}
              </th>
              <th className="px-2 py-3 text-right font-semibold text-gray-700 whitespace-nowrap">
                {t('insurance.vehicleTab.col.insuredValue')}
              </th>
              <th className="px-2 py-3 text-center font-semibold text-gray-700 whitespace-nowrap">
                {t('insurance.vehicleTab.col.verification')}
              </th>
              <th className="px-2 py-3 text-right font-semibold text-gray-700 whitespace-nowrap sticky right-0 bg-gray-50 z-10">
                {t('insurance.vehicleTab.col.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredVehicleInsurance.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                  {t('insurance.vehicleTab.empty')}
                </td>
              </tr>
            ) : (
              filteredVehicleInsurance.map(vi => (
                <tr key={vi.id} className="hover:bg-gray-50">
                  <td className="px-2 py-2 font-medium text-gray-900 sticky left-0 bg-white hover:bg-gray-50 z-10">
                    {getVehicleInfo(vi.vehicle_id)}
                  </td>
                  <td className="px-2 py-2 text-gray-700">{getContractInfo(vi.contract_id)}</td>
                  <td className="px-2 py-2 text-center">
                    {vi.coverage_model_id ? (() => {
                      const model = coverageModels.find(m => m.id === vi.coverage_model_id);
                      const level = model?.level;
                      const label =
                        level === 'tous_risques' ? t('insurance.coverageLevel.tous_risques') :
                        level === 'tiers_plus' ? t('insurance.coverageLevel.tiers_plus') :
                        level === 'tiers_simple' ? t('insurance.coverageLevel.tiers_simple') :
                        level === 'economique' ? t('insurance.coverageLevel.economique') :
                        (model?.name || level || t('insurance.vehicleTab.coverageFallback', { id: vi.coverage_model_id }));
                      return (
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                          {label}
                        </span>
                      );
                    })() : (
                      <span className="text-gray-400 text-xs">-</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-center text-gray-700">
                    {vi.premium_amount ? money(vi.premium_amount, vi) : '-'}
                  </td>
                  <td className="px-2 py-2 text-center text-gray-700">
                    {vi.deductible_amount ? money(vi.deductible_amount, vi) : '-'}
                  </td>
                  <td className="px-2 py-2 text-right font-medium text-gray-900">
                    {vi.valeur_assuree ? money(vi.valeur_assuree, vi) : '-'}
                  </td>
                  <td className="px-2 py-2 text-center">
                    {(() => {
                      const vs = verificationStatuses[vi.id];
                      if (!vs || vs.status === 'not_verified') {
                        return (
                          <button
                            onClick={() => { setVerifyingVi(vi); setShowVerificationModal(true); }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 rounded"
                            title={t('insurance.vehicleTab.notVerifiedTitle')}
                          >
                            <ShieldQuestion className="w-3.5 h-3.5" />
                            {t('insurance.vehicleTab.notVerified')}
                          </button>
                        );
                      }
                      if (vs.status === 'verified_ok') {
                        return (
                          <button
                            onClick={() => { setVerifyingVi(vi); setShowVerificationModal(true); }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-green-700 bg-green-100 hover:bg-green-200 rounded"
                            title={t('insurance.vehicleTab.verifiedTitle')}
                          >
                            <ShieldCheck className="w-3.5 h-3.5" />
                            {t('insurance.vehicleTab.verified')}
                          </button>
                        );
                      }
                      if (vs.status === 'verified_discrepancy') {
                        return (
                          <button
                            onClick={() => { setVerifyingVi(vi); setShowVerificationModal(true); }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-700 bg-red-100 hover:bg-red-200 rounded"
                            title={t('insurance.vehicleTab.discrepancyTitle')}
                          >
                            <ShieldAlert className="w-3.5 h-3.5" />
                            {t('insurance.vehicleTab.discrepancies', { count: vs.discrepancy_count })}
                          </button>
                        );
                      }
                      if (vs.status === 'expired') {
                        return (
                          <button
                            onClick={() => { setVerifyingVi(vi); setShowVerificationModal(true); }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-yellow-700 bg-yellow-100 hover:bg-yellow-200 rounded"
                            title={t('insurance.vehicleTab.expiredTitle')}
                          >
                            <ShieldQuestion className="w-3.5 h-3.5" />
                            {t('insurance.vehicleTab.expiredVerification')}
                          </button>
                        );
                      }
                      return <span className="text-gray-400 text-xs">-</span>;
                    })()}
                  </td>
                  <td className="px-2 py-2 text-right sticky right-0 bg-white hover:bg-gray-50 z-10">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => handleViewContract(vi.contract_id)}
                        className="flex items-center gap-1 px-2 py-1 text-green-600 hover:bg-green-50 rounded text-xs"
                        title={t('insurance.vehicleTab.viewContract')}
                      >
                        <FileText className="w-3 h-3" />
                        <span className="hidden xl:inline">{t('insurance.vehicleTab.col.contract')}</span>
                      </button>
                      <button
                        onClick={() => handleEdit(vi)}
                        className="flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-xs"
                        title={t('insurance.vehicleTab.editAssignment')}
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDelete(vi.id)}
                        className="flex items-center gap-1 px-2 py-1 text-red-600 hover:bg-red-50 rounded text-xs"
                        title={t('insurance.vehicleTab.deleteAssignment')}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <VehicleInsuranceForm
          item={editingItem}
          vehicles={vehicles}
          contracts={contracts}
          onSave={handleSave}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Contract Detail Modal */}
      {selectedContract && (
        <ContractDetailModal
          contract={selectedContract}
          onClose={() => setSelectedContract(null)}
          onUpdate={loadData}
          onMessage={onMessage}
        />
      )}

      {/* Bulk Add Tool */}
      {showAddTool && selectedContractForTool && (
        <VehicleAddTool
          contract={selectedContractForTool}
          onClose={() => {
            setShowAddTool(false);
          }}
          onSuccess={() => {
            loadData();
          }}
          onMessage={onMessage}
        />
      )}

      {/* Bulk Remove Tool */}
      {showRemoveTool && selectedContractForTool && (
        <VehicleRemoveTool
          contract={selectedContractForTool}
          onClose={() => {
            setShowRemoveTool(false);
          }}
          onSuccess={() => {
            loadData();
          }}
          onMessage={onMessage}
        />
      )}

      {/* Insurance Verification Modal */}
      {showVerificationModal && verifyingVi && (
        <InsuranceVerificationModal
          isOpen={showVerificationModal}
          onClose={() => { setShowVerificationModal(false); setVerifyingVi(null); }}
          vehicleId={verifyingVi.vehicle_id}
          vehicleInsuranceId={verifyingVi.id}
          contractId={verifyingVi.contract_id}
          onVerified={() => {
            onMessage?.(t('insurance.vehicleTab.verificationSaved'), 'success');
            loadData();
          }}
        />
      )}
    </div>
  );
}

export default VehicleInsuranceTab;
